
import React, { createContext, useContext, useState, useEffect } from 'react';
import { UserData, VIPTier } from '@/types/slimeHabit';

// 创建一个随机ID函数
function generateUserId() {
  return `user_${Math.random().toString(36).substr(2, 9)}`;
}

// 定义默认用户状态
const defaultUserData: UserData = {
  id: generateUserId(),
  displayName: 'Slimer',
  isVIP: false,
  joinDate: new Date().toISOString(),
  achievements: []
};

// 创建上下文
interface UserContextType {
  userData: UserData;
  updateDisplayName: (name: string) => void;
  becomeVIP: (tier: VIPTier, expirationDate: string) => void;
  cancelVIP: () => void;
  addAchievement: (achievementId: string) => void;
  resetUserData: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

// 创建提供者组件
export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [userData, setUserData] = useState<UserData>(() => {
    // 从本地存储加载数据（如果有）
    const savedData = localStorage.getItem('userData');
    return savedData ? JSON.parse(savedData) : defaultUserData;
  });

  // 当用户数据变化时保存到本地存储
  useEffect(() => {
    localStorage.setItem('userData', JSON.stringify(userData));
  }, [userData]);

  // 更新显示名称
  const updateDisplayName = (name: string) => {
    setUserData(prev => ({ ...prev, displayName: name }));
  };

  // 成为VIP用户
  const becomeVIP = (tier: VIPTier, expirationDate: string) => {
    setUserData(prev => ({
      ...prev,
      isVIP: true,
      vipTier: tier,
      vipExpiration: expirationDate
    }));
  };

  // 取消VIP
  const cancelVIP = () => {
    setUserData(prev => ({
      ...prev,
      isVIP: false,
      vipTier: undefined,
      vipExpiration: undefined
    }));
  };

  // 添加成就
  const addAchievement = (achievementId: string) => {
    setUserData(prev => {
      if (!prev.achievements?.includes(achievementId)) {
        return {
          ...prev,
          achievements: [...(prev.achievements || []), achievementId]
        };
      }
      return prev;
    });
  };

  // 重置用户数据
  const resetUserData = () => {
    setUserData({
      ...defaultUserData,
      id: generateUserId(), // 生成新的用户ID
      joinDate: new Date().toISOString() // 更新加入日期
    });
  };

  return (
    <UserContext.Provider value={{
      userData,
      updateDisplayName,
      becomeVIP,
      cancelVIP,
      addAchievement,
      resetUserData
    }}>
      {children}
    </UserContext.Provider>
  );
};

// 自定义钩子
export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
